import { Injectable } from '@angular/core';
import { CompanyService } from './company.service';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class ColorService {
  private destroy$ = new Subject<void>();

  // Colores por defecto
  private defaultColors = {
    primary: '#4285f4',
    secondary: '#6c757d',
    success: '#34a853',
    danger: '#ea4335',
    warning: '#fbbc04',
    info: '#4fc3f7'
  };

  // Colores de texto por defecto
  private defaultTextColors = {
    primary: '#212529',
    secondary: '#6c757d',
    muted: '#6c757d',
    light: '#f8f9fa',
    dark: '#212529'
  };

  constructor(private companyService: CompanyService) {
    this.initializeColorSystem();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Inicializar el sistema de colores
   */
  private initializeColorSystem() {
    // Aplicar colores por defecto primero
    this.applyDefaultColors();
    this.applyDefaultTextColors();

    // Escuchar cambios en la empresa actual
    this.companyService.currentCompany$
      .pipe(takeUntil(this.destroy$))
      .subscribe(company => {
        if (company && company.appearance) {
          this.applyCompanyColors(company.appearance);
        } else {
          // Si no hay empresa, usar solo colores por defecto
          this.applyDefaultColors();
          this.applyDefaultTextColors();
        }
      });

    // Cargar empresa por defecto al inicio
    this.companyService.loadDefaultCompany()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          // Los colores se aplicarán automáticamente a través del observable currentCompany$
        },
        error: (error) => {
          // Solo usar colores por defecto, no localStorage
          this.applyDefaultColors();
          this.applyDefaultTextColors();
        }
      });
  }

  /**
   * Aplicar colores de la empresa
   */
  private applyCompanyColors(appearance: any) {
    const companyColors = {
      primary: appearance.primaryColor,
      secondary: appearance.secondaryColor,
      success: appearance.successColor,
      danger: appearance.dangerColor,
      warning: appearance.warningColor,
      info: appearance.infoColor
    };

    // Aplicar cada color
    Object.keys(companyColors).forEach(colorType => {
      const color = companyColors[colorType as keyof typeof companyColors];
      if (color) {
        this.updateCSSVariable(colorType, color);
      }
    });
  }

  /**
   * Exponer método público para aplicar colores desde fuera del servicio
   */
  public applyColorsFromAppearance(appearance: any) {
    this.applyCompanyColors(appearance);
  }


  /**
   * Aplicar colores por defecto
   */
  private applyDefaultColors() {
    Object.keys(this.defaultColors).forEach(colorType => {
      this.updateCSSVariable(colorType, this.defaultColors[colorType as keyof typeof this.defaultColors]);
    });
  }

  /**
   * Aplicar colores de texto por defecto
   */
  private applyDefaultTextColors() {
    Object.keys(this.defaultTextColors).forEach(colorType => {
      this.updateTextCSSVariable(colorType, this.defaultTextColors[colorType as keyof typeof this.defaultTextColors]);
    });
  }

  /**
   * Actualizar variables CSS
   */
  private updateCSSVariable(colorType: string, color: string) {
    document.documentElement.style.setProperty(`--bs-${colorType}`, color);
    
    // También actualizar la versión RGB para transparencias
    const rgb = this.hexToRgb(color);
    if (rgb) {
      document.documentElement.style.setProperty(`--bs-${colorType}-rgb`, `${rgb.r}, ${rgb.g}, ${rgb.b}`);
    }
  }

  /**
   * Actualizar variables CSS de texto
   */
  private updateTextCSSVariable(colorType: string, color: string) {
    document.documentElement.style.setProperty(`--bs-body-color-${colorType}`, color);
    
    // También para compatibilidad con variables existentes
    if (colorType === 'primary') {
      document.documentElement.style.setProperty(`--text-primary`, color);
    } else if (colorType === 'secondary') {
      document.documentElement.style.setProperty(`--text-secondary`, color);
    }
    
    // También actualizar la versión RGB para transparencias
    const rgb = this.hexToRgb(color);
    if (rgb) {
      document.documentElement.style.setProperty(`--bs-body-color-${colorType}-rgb`, `${rgb.r}, ${rgb.g}, ${rgb.b}`);
    }
  }

  /**
   * Convertir hex a RGB
   */
  private hexToRgb(hex: string) {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : null;
  }
}